import { ValuationsHome } from '@dealstack/frontend/app/pages/Valuations/views/ValuationsHome/ValuationsHome';
import { InPageContainer } from './styled';

import { generatePath, Route } from 'react-router-dom';
import { EditValuation } from '@dealstack/frontend/app/pages/Valuations/views/EditValuation/EditValuation';
import { useContext, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ValuationSummary } from '@dealstack/frontend/app/pages/Valuations/views/ValuationSummary/ValuationSummary';
import { ValuationSourcesAndUses } from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/ValuationSourcesAndUses';
import { BreakpointSummary } from '@dealstack/frontend/app/pages/Valuations/views/BreakpointSummary/BreakpointSummary';
import {
	BreadcrumbsProvider,
	useBreadcrumbs,
} from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry';
import { DealDropdown } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealDropdown/DealDropdown';
import { QuickSwitchBreadcrumbsNav } from '@dealstack/frontend/shared/components/QuickSwitchBreadcrumbsNav/QuickSwitchBreadcrumbsNav';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils';
import { VALUATIONS_ROUTES } from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import {
	VALUATION_COMPUTE_PROGRESS_QUERY_KEY,
	VALUATION_COMPUTE_QUERY_KEY,
	VALUATION_COMPUTE_START_QUERY_KEY,
	VALUATION_SUMMARY_RESULT_QUERY_KEY,
} from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';

const ValuationsRoot = () => {
	return (
		<InPageContainer>
			<QuickSwitchBreadcrumbsNav />

			<SentryRoutes>
				<Route
					path="/:valuationId/sources-and-uses"
					element={<ValuationSourcesAndUses />}
				/>
				<Route
					path="/:valuationId/sources-and-uses/breakpoint-summary"
					element={<BreakpointSummary />}
				/>
				<Route path="/:valuationId/summary" element={<ValuationSummary />} />
				<Route path="/:valuationId" element={<EditValuation />} />
				<Route index element={<ValuationsHome />} />
			</SentryRoutes>
		</InPageContainer>
	);
};

export const Valuations = () => {
	const tCommonUi = useTranslationCommonUi();
	const { value: inPageFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.IN_PAGE_BREADCRUMBS_AND_QUICK_SWITCH_MENU
	);

	const { deal, deals, dealId } = useContext(DealContext);
	const queryClient = useQueryClient();

	// The compute query is staleTime:Infinity and is NOT invalidated by cap-table edits (those happen
	// on the Cap route, which knows nothing about this cache). So drop the deal's cached compute when
	// this page unmounts - i.e. on a real exit to another deal section - forcing a fresh compute on
	// re-entry against the current cap table. The sibling views navigate as flat routes under here, so
	// this parent survives tab switches (preserving the gcTime dedup) and only unmounts on exit.
	useEffect(() => {
		return () => {
			for (const key of [
				VALUATION_COMPUTE_START_QUERY_KEY,
				VALUATION_COMPUTE_QUERY_KEY,
				VALUATION_COMPUTE_PROGRESS_QUERY_KEY,
				VALUATION_SUMMARY_RESULT_QUERY_KEY,
			]) {
				queryClient.removeQueries({ queryKey: [key, dealId ?? ''] });
			}
		};
	}, [queryClient, dealId]);

	const breadcrumbs = useMemo(() => {
		if (inPageFeatureFlag) {
			return [
				{
					name: deal?.name ?? 'Deal',
					url: generatePath(ROUTES.DEAL, { dealId }),
					element: (
						<DealDropdown
							key="deal-dropdown"
							currentDealId={dealId}
							deals={deals ?? []}
						/>
					),
				},
			];
		} else {
			return [
				...makeDealPageBreadcrumbs({
					tCommonUi,
					dealId,
					dealName: deal?.name,
					deals: deals ?? [],
				}),
				{
					name: tCommonUi('breadcrumbs.valuations'),
					url: VALUATIONS_ROUTES.root({ dealId: dealId ?? '' }),
				},
			];
		}
	}, [deal?.name, dealId, deals, inPageFeatureFlag, tCommonUi]);

	useBreadcrumbs(breadcrumbs, { skipFirst: true });

	return (
		<BreadcrumbsProvider>
			<ValuationsRoot />
		</BreadcrumbsProvider>
	);
};
